/**
 * Meta tags de identificação, SEO e Open Graph.
 *
 * Substitui o `ContentMetadataTags` do Volto no `View.jsx` do projeto. É
 * substituição, e não acréscimo, porque três coisas do core precisavam mudar:
 *
 * - `twitter:card` era `summary_large_image` incondicional, inclusive em
 *   página sem imagem — o card prometia uma foto que não existia;
 * - a resolução de `og:image` lê um único campo
 *   (`settings.contentMetadataTagsImageField`), o que não cobre o portal:
 *   notícia usa `image`, outros tipos podem usar `preview_image_link` ou um
 *   bloco, e a home não tem imagem própria (ver `ogImage.ts`);
 * - o nome do site vinha do expander `navroot`; aqui sai de `state.site`, que
 *   o SSR já carrega, evitando uma dependência a mais.
 *
 * O que o core não emitia e passa a sair daqui: `og:type`, `og:site_name`,
 * `og:locale`, `og:image:alt`, `twitter:title/description/image`, as
 * `article:*` e o identificador único do conteúdo (Cap. III, I).
 */

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import config from '@plone/volto/registry';
import React from 'react';
import { useSelector } from 'react-redux';

import { buildSeoMetadata, SeoMetadataOptions } from './metadata';

interface Props extends Pick<SeoMetadataOptions, 'canonicalPath'> {
  /** Conteúdo exibido — numa pasta com item padrão, é o item resolvido. */
  content: any;
}

export const SeoMetaTags: React.FC<Props> = ({ content, canonicalPath }) => {
  const siteName = useSelector(
    (state: any) => state.site?.data?.['plone.site_title'],
  );

  const meta = buildSeoMetadata(content, {
    siteName,
    canonicalPath,
    fallbackImageUrl: (config.settings as any)?.mpfSeo?.fallbackImageUrl,
  });

  if (!meta) return null;

  const { image } = meta;

  return (
    <Helmet>
      <title>{meta.title}</title>
      {meta.canonicalUrl && <link rel="canonical" href={meta.canonicalUrl} />}
      {meta.description && (
        <meta name="description" content={meta.description} />
      )}
      {/* Cap. III, I — identificador único do CMS, estável mesmo que título,
          endereço ou posição na árvore mudem. */}
      {meta.identifier && (
        <meta name="dcterms.identifier" content={meta.identifier} />
      )}
      <meta name="robots" content={meta.robots} />

      {/* Seção II — Open Graph */}
      {meta.ogTitle && <meta property="og:title" content={meta.ogTitle} />}
      {meta.ogDescription && (
        <meta property="og:description" content={meta.ogDescription} />
      )}
      <meta property="og:type" content={meta.ogType} />
      {meta.canonicalUrl && (
        <meta property="og:url" content={meta.canonicalUrl} />
      )}
      <meta property="og:site_name" content={meta.siteName} />
      <meta property="og:locale" content={meta.ogLocale} />
      {image && <meta property="og:image" content={image.url} />}
      {image?.width && (
        <meta property="og:image:width" content={String(image.width)} />
      )}
      {image?.height && (
        <meta property="og:image:height" content={String(image.height)} />
      )}
      {/* Seção II, VI + Seção VII, I — alternativa textual da imagem de
          pré-visualização. */}
      {image?.alt && <meta property="og:image:alt" content={image.alt} />}

      {meta.publishedTime && (
        <meta property="article:published_time" content={meta.publishedTime} />
      )}
      {meta.modifiedTime && (
        <meta property="article:modified_time" content={meta.modifiedTime} />
      )}
      {meta.section && (
        <meta property="article:section" content={meta.section} />
      )}
      {meta.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      <meta name="twitter:card" content={meta.twitterCard} />
      {meta.ogTitle && <meta name="twitter:title" content={meta.ogTitle} />}
      {meta.ogDescription && (
        <meta name="twitter:description" content={meta.ogDescription} />
      )}
      {image && <meta name="twitter:image" content={image.url} />}
      {image?.alt && <meta name="twitter:image:alt" content={image.alt} />}
    </Helmet>
  );
};

export default SeoMetaTags;
